import React, { createContext, useContext, useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

// Context
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Agency Pages
import AgencyLogin from './pages/agency/Login';
import AgencyDashboard from './pages/agency/Dashboard';
import ClientManagement from './pages/agency/ClientManagement';
import ClientDetail from './pages/agency/ClientDetail';
import ContentCalendar from './pages/agency/ContentCalendar';
import PostEditor from './pages/agency/PostEditor';
import TeamManagement from './pages/agency/TeamManagement';
import AgencySettings from './pages/agency/Settings';
import AgencySuggestions from './pages/agency/Suggestions';

// Client Pages
import ClientLogin from './pages/client/Login';
import ClientInvite from './pages/client/Invite';
import ClientDashboard from './pages/client/Dashboard';
import ClientApprovals from './pages/client/Approvals';
import ClientApprovedPosts from './pages/client/ApprovedPosts';
import ClientPostDetail from './pages/client/PostDetail';
import ClientSettings from './pages/client/Settings';
import ClientForgotPassword from './pages/client/ForgotPassword';
import ClientResetPassword from './pages/client/ResetPassword';
import ClientSuggestions from './pages/client/Suggestions';
import ClientContentCalendar from './pages/client/ContentCalendar';
import AgencyMagicLinkVerify from './pages/agency/MagicLinkVerify';
import ClientMagicLinkVerify from './pages/client/MagicLinkVerify';

// Layouts
import AgencyLayout from './layouts/AgencyLayout';
import ClientLayout from './layouts/ClientLayout';

// Protected Route Components
const AgencyRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }
  
  if (!user || !['superadmin', 'admin'].includes(user.role)) {
    return <Navigate to="/agency/login" replace />;
  }
  
  return <AgencyLayout>{children}</AgencyLayout>;
};

const ClientRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#556B2F]"></div>
      </div>
    );
  }
  
  if (!user || user.role !== 'client') {
    return <Navigate to="/client/login" replace />;
  }
  
  return <ClientLayout>{children}</ClientLayout>;
};

// Landing Page
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f7f0] to-[#e8edda] dark:from-[#131a0b] dark:to-[#1e2711]">
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-8">
            <div className="flex justify-center mb-6">
              <img src="/bda-logo.png" alt="BDA Technologies" className="h-16 w-auto" />
            </div>
            <div className="mb-4">
              <h1 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white tracking-tight">
                ContentDesk
              </h1>
            </div>
            <p className="text-xl text-slate-600 dark:text-slate-400 mb-8 max-w-2xl mx-auto">
              The LinkedIn content management platform built for agencies. 
              Streamline your workflow, delight your clients.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-xl mx-auto">
            <a
              href="/agency/login"
              data-testid="agency-login-link"
              className="group relative p-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg hover:border-[#556B2F] transition-all duration-200"
            >
              <div className="w-12 h-12 bg-[#d8e5b8] dark:bg-[#556B2F]/15 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#556B2F] transition-colors">
                <svg className="w-6 h-6 text-[#556B2F] dark:text-[#b8cf88] group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Agency Portal</h2>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                Manage clients, create content, and track approvals
              </p>
            </a>
            
            <a
              href="/client/login"
              data-testid="client-login-link"
              className="group relative p-6 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg hover:border-[#556B2F] transition-all duration-200"
            >
              <div className="w-12 h-12 bg-[#d8e5b8] dark:bg-[#556B2F]/15 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#556B2F] transition-colors">
                <svg className="w-6 h-6 text-[#3d4f22] dark:text-[#b8cf88] group-hover:text-white transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Client Portal</h2>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                Review and approve your content in seconds
              </p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Landing */}
            <Route path="/" element={<LandingPage />} />
            
            {/* Agency Routes */}
            <Route path="/agency/login" element={<AgencyLogin />} />
            <Route path="/agency/magic-link" element={<AgencyMagicLinkVerify />} />
            <Route path="/agency" element={<AgencyRoute><AgencyDashboard /></AgencyRoute>} />
            <Route path="/agency/dashboard" element={<AgencyRoute><AgencyDashboard /></AgencyRoute>} />
            <Route path="/agency/clients" element={<AgencyRoute><ClientManagement /></AgencyRoute>} />
            <Route path="/agency/clients/:clientId" element={<AgencyRoute><ClientDetail /></AgencyRoute>} />
            <Route path="/agency/calendar" element={<AgencyRoute><ContentCalendar /></AgencyRoute>} />
            <Route path="/agency/calendar/:clientId" element={<AgencyRoute><ContentCalendar /></AgencyRoute>} />
            <Route path="/agency/posts/new" element={<AgencyRoute><PostEditor /></AgencyRoute>} />
            <Route path="/agency/posts/:postId" element={<AgencyRoute><PostEditor /></AgencyRoute>} />
            <Route path="/agency/team" element={<AgencyRoute><TeamManagement /></AgencyRoute>} />
            <Route path="/agency/suggestions" element={<AgencyRoute><AgencySuggestions /></AgencyRoute>} />
            <Route path="/agency/settings" element={<AgencyRoute><AgencySettings /></AgencyRoute>} />
            
            {/* Client Routes */}
            <Route path="/client/login" element={<ClientLogin />} />
            <Route path="/client/magic-link" element={<ClientMagicLinkVerify />} />
            <Route path="/client/forgot-password" element={<ClientForgotPassword />} />
            <Route path="/client/reset-password/:token" element={<ClientResetPassword />} />
            <Route path="/client/invite/:token" element={<ClientInvite />} />
            <Route path="/client" element={<ClientRoute><ClientDashboard /></ClientRoute>} />
            <Route path="/client/dashboard" element={<ClientRoute><ClientDashboard /></ClientRoute>} />
            <Route path="/client/approvals" element={<ClientRoute><ClientApprovals /></ClientRoute>} />
            <Route path="/client/approved" element={<ClientRoute><ClientApprovedPosts /></ClientRoute>} />
            <Route path="/client/suggestions" element={<ClientRoute><ClientSuggestions /></ClientRoute>} />
            <Route path="/client/calendar" element={<ClientRoute><ClientContentCalendar /></ClientRoute>} />
            <Route path="/client/posts/:postId" element={<ClientRoute><ClientPostDetail /></ClientRoute>} />
            <Route path="/client/settings" element={<ClientRoute><ClientSettings /></ClientRoute>} />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" closeButton />
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
